import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API_BASE from '../config';

const RULES = [
  { test: (v) => v.length >= 10, label: 'At least 10 characters' },
  { test: (v) => /[A-Z]/.test(v), label: 'At least 1 uppercase letter' },
  { test: (v) => /[a-z]/.test(v), label: 'At least 1 lowercase letter' },
  { test: (v) => /[0-9]/.test(v), label: 'At least 1 number' },
  { test: (v) => /[^A-Za-z0-9]/.test(v), label: 'At least 1 symbol (!@#$%^&*)' },
];

export default function ChangePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { changePwToken, reason } = location.state || {};

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!changePwToken) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <h2 style={s.title}>Session Expired</h2>
          <p style={{ color: '#64748b' }}>Please log in again.</p>
          <button onClick={() => navigate('/login')} style={s.btn}>Go to Login</button>
        </div>
      </div>
    );
  }

  const allPassed = RULES.every((r) => r.test(password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allPassed) { setError('Password does not meet all requirements'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ changePwToken, newPassword: password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Failed'); return; }

      // Save token and redirect to dashboard
      localStorage.setItem('token', data.token);
      window.location.href = '/';
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔒</div>
          <h2 style={s.title}>
            {reason === 'one-time' ? 'Set Your New Password' : 'Password Expired'}
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
            {reason === 'one-time'
              ? 'Your account has a one-time password. Please create a new password to continue.'
              : 'Your password has expired. Please set a new password (passwords expire every 2 months).'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={s.label}>New Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={s.input}
            placeholder="Enter new password"
            autoFocus
          />

          {/* Real-time rule checklist */}
          <div style={{ margin: '12px 0 18px', padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            {RULES.map((r, i) => {
              const pass = r.test(password);
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: '0.85rem', color: pass ? '#16a34a' : '#94a3b8' }}>
                  <span>{pass ? '✓' : '○'}</span>
                  <span>{r.label}</span>
                </div>
              );
            })}
          </div>

          <label style={s.label}>Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            style={s.input}
            placeholder="Confirm new password"
          />

          {error && <div style={s.err}>{error}</div>}

          <button type="submit" disabled={loading || !allPassed || password !== confirm} style={{ ...s.btn, opacity: (loading || !allPassed || password !== confirm) ? 0.6 : 1 }}>
            {loading ? 'Saving...' : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: 16 },
  card: { background: '#fff', borderRadius: 12, padding: '32px 28px', maxWidth: 420, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' },
  title: { margin: '0 0 4px', fontSize: '1.4rem', color: '#1e293b', fontWeight: 700 },
  label: { display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '0.85rem', color: '#374151' },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.95rem', marginBottom: 12, boxSizing: 'border-box' },
  btn: { width: '100%', padding: '11px 0', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', marginTop: 8 },
  err: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '8px 14px', borderRadius: 8, marginBottom: 8, fontSize: '0.85rem' },
};
